// encryption.utils.ts
const salt = new TextEncoder().encode('your-fixed-app-salt'); // App-wide salt (can randomize per user later)

export async function deriveKeyFromPassphrase(passphrase: string): Promise<CryptoKey> {
	const encoder = new TextEncoder();
	const keyMaterial = await crypto.subtle.importKey(
		'raw',
		encoder.encode(passphrase),
		'PBKDF2',
		false,
		['deriveKey']
	);

	return crypto.subtle.deriveKey(
		{
			name      : 'PBKDF2',
			salt,
			iterations: 150000, // High iterations for security
			hash      : 'SHA-256'
		},
		keyMaterial,
		{
			name  : 'AES-GCM',
			length: 256
		},
		true, // ✨ Set to true to allow key extraction
		[
			'encrypt',
			'decrypt'
		]
	);
}

export async function encryptString(key: CryptoKey, data: string): Promise<string> {
	const encoder = new TextEncoder();
	const encodedData = encoder.encode(data);
	const iv = crypto.getRandomValues(new Uint8Array(12)); // 12-byte IV

	const ciphertext = await crypto.subtle.encrypt(
		{
			name: 'AES-GCM',
			iv
		},
		key,
		encodedData
	);

	// Combine IV and ciphertext into one buffer
	const combined = new Uint8Array(iv.length + ciphertext.byteLength);
	combined.set(iv, 0);
	combined.set(new Uint8Array(ciphertext), iv.length);

	// Convert to base64
	const binary = String.fromCharCode(...combined);
	return btoa(binary);
}

export async function decryptString(key: CryptoKey, encryptedData: string): Promise<string> {
	const combined = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0));

	const iv = combined.slice(0, 12); // Extract IV
	const ciphertext = combined.slice(12);

	const decrypted = await crypto.subtle.decrypt(
		{
			name: 'AES-GCM',
			iv
		},
		key,
		ciphertext
	);

	const decoder = new TextDecoder();
	return decoder.decode(decrypted);
}